import type { HubEntityScope } from "./hubPermissions";

export enum SupportCaseStatus {
  Open = "open",
  InProgress = "inProgress",
  Pending = "pending",
  Resolved = "resolved",
  Closed = "closed",
}

export enum SupportCaseSeverity {
  High = "high",
  Medium = "medium",
  Low = "low",
}

export const supportCaseSeverities = Object.values(SupportCaseSeverity);

export const supportAttachmentMaxBytes = 10 * 1024 * 1024;

export type SupportCase = Readonly<{
  caseId: string;
  ticketNumber: string;
  title: string;
  /** CRM subject GUID; see constants/supportSubjects. */
  subjectId?: string;
  severity?: SupportCaseSeverity;
  status: SupportCaseStatus;
  statusLabel: string;
  createdOn: string;
  modifiedOn: string;
  createdByName: string;
  /** Primary case contact id; seeds the avatar color. */
  createdById?: string;
  isOwn: boolean;
  gameConceptId?: string;
  gameName?: string;
  /** Newest email sent to the partner by Xbox support. */
  lastSupportReplyOn?: string;
  /** Newest email or reply sent by the partner. */
  lastPartnerReplyOn?: string;
  latestMessagePreview?: string;
}>;

export type SupportCaseList = Readonly<{
  scope: HubEntityScope;
  /** Cases across all pages; `cases` holds the pages loaded so far. */
  totalCount: number;
  hasMore: boolean;
  cases: ReadonlyArray<SupportCase>;
}>;

export type SupportAttachment = Readonly<{
  attachmentId: string;
  name: string;
  size: number;
  mimeType: string;
}>;

// Raster images only: SVG can carry script, so it downloads like any other file.
export const previewableImageTypes: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
]);

export type SupportCaseDetails = Readonly<{
  supportCase: SupportCase;
  description: string;
  attachments: ReadonlyArray<SupportAttachment>;
  /** Primary case contact, shown in the details panel. */
  submittedBy?: Readonly<{ contactId: string; firstName: string; lastName: string; email: string }>;
  /** Local development only: opens the case in the CRM instance the server targets. */
  crmUrl?: string;
}>;

export type SupportGame = Readonly<{
  gameConceptId: string;
  name: string;
}>;

export type CreateSupportCaseRequest = Readonly<{
  title: string;
  subjectId: string;
  severity: SupportCaseSeverity;
  description: string;
  gameConceptId?: string;
}>;

export type SupportCaseMessage = Readonly<{
  messageId: string;
  fromXbox: boolean;
  senderName: string;
  senderAccount?: string;
  sentOn: string;
  body: string;
  attachmentCount: number;
  attachments?: ReadonlyArray<SupportAttachment>;
}>;

export type SupportCaseReplyTarget = Readonly<{
  address: string;
  subject: string;
  cc?: ReadonlyArray<string>;
}>;

/** One page of a case conversation; page 1 holds the newest messages. */
export type SupportCaseThread = Readonly<{
  messages: ReadonlyArray<SupportCaseMessage>;
  page: number;
  totalPages: number;
  /** CRM email tracking token (e.g. "XBOX:040400000008") that correlates replies to the conversation. */
  trackingToken?: string;
  replyTo?: SupportCaseReplyTarget;
}>;
